// EN mirror of /thai-visa — Thai visa hub
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookMarked as Passport } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { BRAND_URL } from "@/lib/brand";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const TITLE = "Thai Visa Services 2026 — Non-B, LTR, DTV, Retirement, Marriage | NYC";
const DESC = "End-to-end Thai visa handling: Non-B work permit, LTR (Long-Term Resident), DTV remote worker. Ask us anything — real experts reply within minutes.";

const VISAS = [
  { slug: "non-b", name: "Non-B (Work / Business)", days: "9–14 business days", notes: "Requires WP.3 corporate letter, sponsor VAT 20, org chart. Rush appointment via Chaengwattana counter." },
  { slug: "ltr", name: "LTR — Long-Term Resident (10-year)", days: "45–60 days BOI review", notes: "Four categories: Wealthy Global Citizen, Wealthy Pensioner, Work-from-Thailand, Highly-Skilled. Requires USD 80k income proof / USD 500k assets for most tracks." },
  { slug: "dtv", name: "DTV — Destination Thailand Visa (5-year, remote worker)", days: "15–30 days", notes: "Portfolio of remote-work clients, THB 500k bank balance, no employer sponsorship needed. Multiple entry, 180-day per stay." },
  { slug: "retirement-o", name: "Non-O Retirement (50+)", days: "10–15 days", notes: "THB 800k in Thai bank OR THB 65k/month pension income. Annual extension." },
  { slug: "marriage-o", name: "Non-O Marriage (Thai spouse)", days: "10–20 days", notes: "Requires Kor Ror 2 (marriage certificate), THB 400k in Thai bank OR THB 40k/month income. Annual extension." },
  { slug: "ed", name: "ED (Education)", days: "5–10 days", notes: "Enrollment letter from MOE-accredited institution. 6-month / 12-month blocks." },
  { slug: "smart", name: "Smart Visa (S/T/I/E/O)", days: "30–60 days", notes: "Targeted industries: BIOTECH, ROBOTICS, AI, next-gen automotive. Requires ministry endorsement." },
];

export const Route = createFileRoute("/en/thai-visa/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/thai-visa", "en")}, { name: "description", content: ctaDesc(DESC, "/en/thai-visa", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/thai-visa", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/thai-visa", "en")},
      { property: "og:url", content: `${BRAND_URL}/en/thai-visa` }, { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${BRAND_URL}/en/thai-visa` },
      ...hreflangLinks("/thai-visa", "/en/thai-visa"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org", "@type": "Service",
        name: "Thai Visa Services — Non-B, LTR, DTV, Retirement & Marriage",
        serviceType: "Thai Visa Application Support",
        provider: { "@type": "LegalService", name: "NYC Language Institute", url: `${BRAND_URL}` },
        areaServed: { "@type": "Country", name: "Thailand" },
        url: `${BRAND_URL}/en/thai-visa`, inLanguage: "en",
      }),
    }],
  }),
  component: EnThaiVisa,
});

function EnThaiVisa() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-14 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">Thai <span className="text-amber-300">visa</span> services</h1>
          <p className="mt-3 max-w-2xl text-white/80">
            Every Thai visa route we handle in-house — from document translation and MFA legalization to the physical counter submission at Chaengwattana. First-try approval focus.
          </p>
        </div>
      </section>
      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2">
          {VISAS.map((v) => (
            <article key={v.slug} className="rounded-2xl border border-slate-200 bg-white p-5">
              <Passport className="h-6 w-6 text-amber-600" aria-hidden />
              <h2 className="mt-2 text-base font-bold text-navy-deep">{v.name}</h2>
              <p className="mt-1 text-xs font-semibold text-emerald-700">{v.days}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">{v.notes}</p>
              <Link to="/en/visa-comparison" className="mt-3 inline-block text-xs font-bold text-amber-700">Compare visas →</Link>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-10 max-w-6xl"><RelatedHubGrid locale="en" /></div>
      </section>
    </main>
  );
}
